import axios from 'axios';

// Use the Netlify function directly instead of relying on redirects
const API_URL = '/.netlify/functions/api-proxy';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Add withCredentials for CORS
  withCredentials: false
});

export interface ScanResponseData {
  message: string;
  scanId: string;
  status: string;
}

export interface Vulnerability {
  id: string;
  name: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  recommendation: string;
  details?: {
    vulnerableEndpoints?: string[];
    formUrls?: string[];
    formCount?: number;
    scriptCount?: number;
    samples?: string[];
    vulnerablePatterns?: string[];
    emailCount?: number;
    sampleEmails?: string[];
    commentSamples?: string[];
    exposedPaths?: string[];
    note?: string;
    cookieCount?: number;
    affectedCookies?: string[];
    exploitInfo?: string;
    exploitExample?: string;
    exploitScenario?: string;
    exploitSteps?: string[];
    sensitiveInfoTypes?: string[];
    systemInfo?: string;
    additionalRisks?: string;
    remediationSteps?: string[];
    remediationExample?: string;
    samplesFound?: string[];
    fieldCount?: number;
    credentialTypes?: string[];
    vulnerableContext?: string;
    exploitMitigation?: string;
    aiEnhanced?: boolean;
    [key: string]: any;
  };
  foundAt?: string; // URL or resource where the vulnerability was found
  cwe?: string; // Common Weakness Enumeration ID
  cvss?: {
    score: number;
    vector: string;
  }; // Common Vulnerability Scoring System
  falsePositivePotential?: 'low' | 'medium' | 'high'; // Likelihood of false positive
  exploitability?: 'easy' | 'moderate' | 'difficult'; // How easy it is to exploit
  remediationComplexity?: 'low' | 'medium' | 'high'; // How difficult it is to fix
}

export interface ScanResult {
  id: string;
  url: string;
  timestamp: Date;
  status: 'pending' | 'scanning' | 'completed' | 'failed';
  vulnerabilities: Vulnerability[];
  summary: {
    total: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  scanDuration?: number;
  error?: string;
  targetMetadata?: {
    serverInfo?: string;
    technologies?: string[];
    headers?: Record<string, string>;
    redirectChain?: string[];
    responseTime?: number;
    statusCode?: number;
    ipAddress?: string;
  };
}

export interface OpenAIStatus {
  enabled: boolean;
}

export interface ChatResponse {
  response: string;
}

// API functions
export const apiService = {
  // Start a new vulnerability scan
  startScan: async (url: string): Promise<ScanResponseData> => {
    try {
      console.log('Sending scan request for URL:', url);
      console.log('API URL being used:', API_URL);
      
      const response = await api.post('/scan', { url });
      console.log('Scan response received:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error starting scan:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        }
      });
      throw error;
    }
  },

  // Get scan results by ID
  getScanResult: async (scanId: string): Promise<ScanResult> => {
    try {
      const response = await api.get(`/scan/${scanId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting scan results:', error);
      throw error;
    }
  },

  // Check if OpenAI integration is enabled
  getOpenAIStatus: async (): Promise<OpenAIStatus> => {
    try {
      const response = await api.get('/openai/status');
      return response.data;
    } catch (error) {
      console.error('Error checking OpenAI status:', error);
      throw error;
    }
  },

  // Send a chat message to OpenAI for processing
  sendChatMessage: async (scanId: string, message: string): Promise<ChatResponse> => {
    try {
      const response = await api.post(`/openai/chat/${scanId}`, { message });
      return response.data;
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw error;
    }
  },

  // Health check
  healthCheck: async (): Promise<{ status: string; message: string }> => {
    try {
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  },
};

export default apiService; 